<template>
  <div class="page-container">
    <main :class="['main-content', { nested: isMemberCenterContext }]">
      <div class="page-inner-box">
        <div class="my-address-container">
          <div class="content-main">
            <div class="address-section">
              <div class="section-header">
                <div class="section-title">地址管理</div>
              </div>
              
              <!-- 地址列表 -->
              <div class="address-list">
                <div 
                  v-for="address in addressList" 
                  :key="address.id" 
                  class="address-card"
                  :class="{ 'is-default': address.isDefault }"
                  @click="editAddress(address)"
                >
                  <!-- 默认地址标签 -->
                  <div v-if="address.isDefault" class="default-tag">
                    默认
                  </div>
                  
                  <div class="address-content">
                    <div class="address-header">
                      <div class="recipient-name">{{ address.name }}</div>
                      <!-- 选中对勾 -->
                      <div v-if="address.isDefault" class="selected-check">
                        <i class="el-icon-check"></i>
                      </div>
                    </div>
                    
                    <div class="recipient-phone">{{ address.phone }}</div>
                    
                    <div class="address-detail">
                      {{ address.province }} {{ address.city }} {{ address.district }}{{ address.detail }}
                    </div>
                  </div>
                </div>

                <!-- 添加新地址卡片 -->
                <div class="add-address-card" @click="addNewAddress">
                  <div class="add-icon">
                    <i class="el-icon-plus"></i>
                  </div>
                  <div class="add-text">添加新地址</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 弹窗部分保持不变，但为了配合UI还原，样式可能会有微调，这里暂且保留原有逻辑 -->
    <el-dialog
      class="address-dialog"
      :title="dialogTitle"
      :visible.sync="showAddAddressDialog"
      width="620px"
      center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-form :model="newAddress" :rules="addressRules" ref="addressForm">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="姓名" prop="name">
              <el-input v-model="newAddress.name" placeholder="请输入姓名"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="手机号" prop="phone">
              <el-input v-model="newAddress.phone" placeholder="请输入手机号"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item label="请选择省/市/区" prop="region">
          <el-cascader
            v-model="newAddress.region"
            :options="regionOptions"
            :props="{ expandTrigger: 'hover', value: 'value', label: 'label' }"
            placeholder="请选择省/市/区"
          ></el-cascader>
        </el-form-item>
        
        <el-form-item label="详细地址" prop="detail">
          <el-input
            v-model="newAddress.detail"
            type="textarea"
            :rows="3"
            placeholder="请输入详细地址"
          ></el-input>
        </el-form-item>
        
        <el-form-item>
          <el-checkbox v-model="newAddress.isDefault">设为默认</el-checkbox>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button
          v-if="newAddress && newAddress.id != null && newAddress.id !== ''"
          type="danger"
          @click="deleteCurrentAddress"
        >删除</el-button>
        <el-button @click="showAddAddressDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmAddAddress">确定</el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
// 导入省市区数据
import regionOptions from '@/assets/json/regions.json'
import { getMyAddresses, createMyAddress, deleteMyAddress, updateMyAddress } from '@/api/authApi'

export default {
  name: 'AddressManagement',
  computed: {
    isMemberCenterContext() {
      return this.$route.path.startsWith('/member-center')
    },
    dialogTitle() {
      return this.newAddress && this.newAddress.id != null && this.newAddress.id !== '' ? '编辑收货地址' : '添加收货地址'
    }
  },
  created() {
    this.loadAddressList()
  },
  data() {
    return {
      // 地址数据
      addressList: [],
      // 弹窗状态
      showAddAddressDialog: false,
      // 新地址表单数据
      newAddress: {
        id: null,
        name: '',
        phone: '',
        region: [],
        detail: '',
        isDefault: false,
        postalCode: ''
      },
      // 使用导入的省市区数据
      regionOptions,
      regionValueLabelMap: null,
      // 表单验证规则
      addressRules: {
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' },
          { min: 2, max: 20, message: '姓名长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3456789]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
        ],
        region: [
          { required: true, message: '请选择省/市/区', trigger: 'change' }
        ],
        detail: [
          { required: true, message: '请输入详细地址', trigger: 'blur' },
          { min: 5, max: 200, message: '详细地址长度在 5 到 200 个字符', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    ensureRegionValueLabelMap() {
      if (this.regionValueLabelMap) return
      const map = Object.create(null)
      const walk = (nodes) => {
        if (!Array.isArray(nodes)) return
        nodes.forEach((n) => {
          if (!n) return
          const value = n.value != null ? String(n.value) : ''
          const label = n.label != null ? String(n.label) : ''
          if (value && label) map[value] = label
          if (Array.isArray(n.children) && n.children.length) walk(n.children)
        })
      }
      walk(this.regionOptions)
      this.regionValueLabelMap = map
    },
    getRegionLabelByValue(value) {
      this.ensureRegionValueLabelMap()
      const v = value != null ? String(value) : ''
      return (this.regionValueLabelMap && this.regionValueLabelMap[v]) || ''
    },
    getRegionLabelsByValues(values) {
      const arr = Array.isArray(values) ? values : []
      return arr.map(v => this.getRegionLabelByValue(v) || (v != null ? String(v) : '')).filter(Boolean)
    },
    getRegionValuesByLabels(labels) {
      const arr = Array.isArray(labels) ? labels.map(v => String(v || '').trim()).filter(Boolean) : []
      if (arr.length === 0) return []
      let nodes = this.regionOptions
      const values = []
      for (let i = 0; i < arr.length; i += 1) {
        const label = arr[i]
        const hit = Array.isArray(nodes) ? nodes.find(n => n && String(n.label) === label) : null
        if (!hit) return []
        values.push(String(hit.value))
        nodes = hit.children
      }
      return values
    },
    normalizeAddress(address, index) {
      const id =
        (address && (address.id ?? address.addressId ?? address.address_id ?? address.studentAddressId ?? address.student_address_id)) ??
        index

      const name = (address && (address.name ?? address.receiverName ?? address.consignee ?? address.contactName ?? address.realName)) || ''
      const phone = (address && (address.phone ?? address.mobile ?? address.receiverPhone ?? address.contactPhone)) || ''
      const provinceRaw = (address && (address.province ?? address.provinceName ?? address.province_name)) || ''
      const cityRaw = (address && (address.city ?? address.cityName ?? address.city_name)) || ''
      const districtRaw = (address && (address.district ?? address.districtName ?? address.district_name)) || ''
      const provinceCodeRaw = (address && (address.provinceCode ?? address.province_code)) || ''
      const cityCodeRaw = (address && (address.cityCode ?? address.city_code)) || ''
      const districtCodeRaw = (address && (address.districtCode ?? address.district_code)) || ''
      
      this.ensureRegionValueLabelMap()
      
      let provinceValue = ''
      let cityValue = ''
      let districtValue = ''
      let province = ''
      let city = ''
      let district = ''
      
      const provinceCodeStr = String(provinceCodeRaw || '')
      const cityCodeStr = String(cityCodeRaw || '')
      const districtCodeStr = String(districtCodeRaw || '')
      const provinceStr = String(provinceRaw || '')
      const cityStr = String(cityRaw || '')
      const districtStr = String(districtRaw || '')
      
      if (provinceCodeStr && this.regionValueLabelMap[provinceCodeStr]) {
        provinceValue = provinceCodeStr
        province = this.regionValueLabelMap[provinceCodeStr]
      } else if (this.regionValueLabelMap[provinceStr]) {
        provinceValue = provinceStr
        province = this.regionValueLabelMap[provinceStr]
      } else {
        province = provinceStr
        provinceValue = provinceStr
      }
      
      if (cityCodeStr && this.regionValueLabelMap[cityCodeStr]) {
        cityValue = cityCodeStr
        city = this.regionValueLabelMap[cityCodeStr]
      } else if (this.regionValueLabelMap[cityStr]) {
        cityValue = cityStr
        city = this.regionValueLabelMap[cityStr]
      } else {
        city = cityStr
        cityValue = cityStr
      }
      
      if (districtCodeStr && this.regionValueLabelMap[districtCodeStr]) {
        districtValue = districtCodeStr
        district = this.regionValueLabelMap[districtCodeStr]
      } else if (this.regionValueLabelMap[districtStr]) {
        districtValue = districtStr
        district = this.regionValueLabelMap[districtStr]
      } else {
        district = districtStr
        districtValue = districtStr
      }
      
      const detail =
        (address && (address.detail ?? address.detailAddress ?? address.addressDetail ?? address.address ?? address.fullAddress)) || ''

      const isDefaultRaw =
        address && (address.isDefault ?? address.default ?? address.is_default ?? address.defaultFlag ?? address.default_flag)

      return {
        id,
        name,
        phone,
        provinceValue,
        cityValue,
        districtValue,
        province,
        city,
        district,
        detail,
        isDefault: Boolean(isDefaultRaw),
        postalCode: address && (address.postalCode ?? address.postal_code ?? address.zipCode ?? address.zip_code)
      }
    },
    async loadAddressList() {
      try {
        this.ensureRegionValueLabelMap()
        const res = await getMyAddresses()
        const data = res && res.data
        const list = Array.isArray(data)
          ? data
          : (data && (data.list || data.records || data.items)) ||
            (data && typeof data === 'object' && (data.address || data.province || data.city || data.district) ? [data] : [])
        this.addressList = list.map((item, index) => this.normalizeAddress(item, index))
      } catch (err) {
        this.addressList = []
        this.$message.error((err && err.message) || '地址加载失败')
      }
    },

    addNewAddress() {
      this.showAddAddressDialog = true
      this.$nextTick(() => {
        if (this.$refs.addressForm) this.$refs.addressForm.clearValidate()
        this.newAddress = {
          id: null,
          name: '',
          phone: '',
          region: [],
          detail: '',
          isDefault: false,
          postalCode: ''
        }
      })
    },
    editAddress(address) {
      const a = address || {}
      this.showAddAddressDialog = true
      this.$nextTick(() => {
        if (this.$refs.addressForm) this.$refs.addressForm.clearValidate()
        
        this.ensureRegionValueLabelMap()
        
        let regionValues = []
        
        if (a.provinceValue && a.cityValue && a.districtValue) {
          const pv = String(a.provinceValue)
          const cv = String(a.cityValue)
          const dv = String(a.districtValue)
          if (this.regionValueLabelMap[pv] && this.regionValueLabelMap[cv] && this.regionValueLabelMap[dv]) {
            regionValues = [pv, cv, dv]
          }
        }
        
        if (!regionValues.length && a.province && a.city && a.district) {
          const labels = [a.province, a.city, a.district]
          regionValues = this.getRegionValuesByLabels(labels)
        }
        
        this.newAddress = {
          id: a.id ?? null,
          name: a.name || '',
          phone: a.phone || '',
          region: regionValues,
          detail: a.detail || '',
          isDefault: Boolean(a.isDefault),
          postalCode: a.postalCode == null ? '' : String(a.postalCode)
        }
      })
    },
    async deleteAddress(addressId) {
      try {
        await this.$confirm('确定要删除该地址吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        this.$message({
          type: 'info',
          message: '已取消删除'
        })
        return
      }
      try {
        await deleteMyAddress(addressId)
        this.$message({
          type: 'success',
          message: '地址删除成功'
        })
        await this.loadAddressList()
      } catch (err) {
        this.$message.error((err && err.message) || '地址删除失败')
      }
    },
    async deleteCurrentAddress() {
      const id = this.newAddress && this.newAddress.id
      if (id == null || id === '') {
        this.$message.error('缺少地址ID，无法删除')
        return
      }
      await this.deleteAddress(id)
      this.showAddAddressDialog = false
    },
    setDefault(addressId) {
      // 设置默认地址逻辑
      this.addressList.forEach(address => {
        address.isDefault = address.id === addressId
      })
      this.$message({
        message: '已设置为默认地址',
        type: 'success'
      })
    },

    confirmAddAddress() {
      // 表单验证
      this.$refs.addressForm.validate(async (valid) => {
        if (valid) {
          const postalCodeRaw = String(this.newAddress.postalCode || '').trim()
          const postalCode = postalCodeRaw ? Number(postalCodeRaw) : undefined
          const regionLabels = this.getRegionLabelsByValues(this.newAddress.region)
          const regionValues = this.newAddress.region || []

          const addressInfo = {
            receiverName: this.newAddress.name,
            receiverPhone: this.newAddress.phone,
            province: regionLabels[0] || '',
            city: regionLabels[1] || '',
            district: regionLabels[2] || '',
            provinceCode: regionValues[0] || '',
            cityCode: regionValues[1] || '',
            districtCode: regionValues[2] || '',
            detailAddress: this.newAddress.detail,
            isDefault: this.newAddress.isDefault ? 1 : 0,
            postalCode
          }

          const isEdit = !(this.newAddress.id == null || this.newAddress.id === '')
          try {
            if (isEdit) {
              await updateMyAddress({ ...addressInfo, id: this.newAddress.id })
            } else {
              await createMyAddress(addressInfo)
            }
            this.showAddAddressDialog = false
            this.$message({
              message: isEdit ? '地址更新成功' : '地址添加成功',
              type: 'success'
            })
            await this.loadAddressList()
          } catch (err) {
            this.$message.error((err && err.message) || (isEdit ? '地址更新失败' : '地址添加失败'))
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; padding-top: 20px; }
.main-content.nested { margin-top: 0; }
.main-content.nested .page-inner-box { width: 100%; margin: 0; }

.my-address-container {
  display: flex;
  gap: 24px;
}

/* 主内容区样式 */
.content-main {
  flex: 1;
}

/* 地址管理 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #3E2CCD;
}

/* 地址列表 */
.address-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

/* 地址卡片 */
.address-card {
  position: relative;
  width: 320px;
  height: 180px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 20px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }
  
  &.is-default {
    border: 1px solid #3E2CCD;
    box-shadow: 0 2px 8px rgba(62, 44, 205, 0.1);
  }
}

.default-tag {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #3E2CCD;
  color: white;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 0 8px 0 8px;
  z-index: 1;
}

.address-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.recipient-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.selected-check {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 24px;
  height: 24px;
  background: #3E2CCD;
  border-radius: 8px 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    color: #fff;
    font-size: 14px;
  }
}

.recipient-phone {
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
}

.address-detail {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-top: 5px;
}

/* 添加新地址卡片 */
.add-address-card {
  width: 320px;
  height: 180px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    border-color: #3E2CCD;
    color: #3E2CCD;
    
    .add-icon {
      color: #3E2CCD;
    }
    
    .add-text {
      color: #3E2CCD;
    }
  }
}

.add-icon {
  font-size: 30px;
  color: #e0e0e0;
  margin-bottom: 10px;
  transition: color 0.3s;
}

.add-text {
  font-size: 14px;
  color: #e0e0e0;
  transition: color 0.3s;
}

/* 弹窗样式（按图还原） */
.address-dialog { border-radius: 12px; }
.address-dialog ::v-deep .el-dialog__header { border-bottom: none; padding: 16px 20px 8px; }
.address-dialog ::v-deep .el-dialog__title { font-size: 16px; font-weight: 500; color: #303133; }
.address-dialog ::v-deep .el-dialog__body { padding: 20px; }
.address-dialog ::v-deep .el-form { max-width: 560px; margin: 0 auto; }
.address-dialog ::v-deep .el-form-item__label { display: none; }
.address-dialog ::v-deep .el-form-item__content { margin-left: 0 !important; }
.address-dialog ::v-deep .el-form-item { margin-bottom: 16px; }
.address-dialog ::v-deep .el-cascader { width: 100%; }
.address-dialog ::v-deep .el-input, .address-dialog ::v-deep .el-textarea { width: 100%; }
.address-dialog ::v-deep .el-input__inner { height: 44px; border: 1px solid var(--theme-primary, #6c5fd6); border-radius: 8px; width: 100%; }
.address-dialog ::v-deep .el-textarea__inner { min-height: 110px; border: 1px solid var(--theme-primary, #6c5fd6); border-radius: 8px; width: 100%; }
.address-dialog ::v-deep .el-input.is-active .el-input__inner,
.address-dialog ::v-deep .el-input__inner:focus,
.address-dialog ::v-deep .el-textarea__inner:focus { border-color: var(--theme-primary, #6c5fd6); box-shadow: 0 0 0 2px rgba(62,44,205,0.08); }
.address-dialog ::v-deep .el-cascader .el-input__inner { height: 44px; }
.address-dialog ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner { border-color: var(--theme-primary, #6c5fd6); background-color: var(--theme-primary, #6c5fd6); }
.address-dialog ::v-deep .el-checkbox__inner { border-radius: 4px; }
.address-dialog ::v-deep .el-checkbox__label { color: #606266; }
.address-dialog ::v-deep .el-dialog__footer { padding: 10px 20px 20px; text-align: center; }
.address-dialog ::v-deep .dialog-footer { display: flex; justify-content: center; gap: 20px; }
.address-dialog ::v-deep .el-dialog__footer .el-button { height: 40px; min-width: 96px; }
.address-dialog ::v-deep .el-dialog__footer .el-button:not(.el-button--primary) { background: #fff; border: 1px solid #eaeaf2; color: #606266; }
/* 保留原有通用按钮样式 */
.el-button--primary {
  background-color: #409eff;
  border-color: #409eff;
}
.el-button--primary:hover,
.el-button--primary:focus {
  background-color: #66b1ff;
  border-color: #66b1ff;
}
</style>
